import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Tooltip, makeStyles } from '@material-ui/core'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import { CHART_CATALOG, resolveCatalogEntry } from '../lib/chartCatalog'
import { useChartCatalogActions } from '../hooks/useChartCatalogActions'
import { usePortfolioData } from './PortfolioDataProvider'
import { useUtilityPanel } from './UtilityPanelProvider'
import { TOOL_REGISTRY } from './tools/toolRegistry'

// `Charts` trigger + menu. Three sections:
//
//   1. Show/hide charts panel toggle (always at top).
//   2. Chart types -- Allocation / Trend / Compare. Each click adds a
//      new blank-payload instance of that type.
//   3. Presets -- the curated combos from CHART_CATALOG. Each click
//      adds a new instance pre-configured with the preset's payload.
//      Duplicates are intentional (instance model): the same preset
//      can be saved multiple times, each tile configurable
//      independently. Removal happens from the tile chrome, not here.
//
// Trigger styling mirrors UtilityMenuButton -- keep numeric padding /
// typography in sync if Manage / Utility shift.

const useStyles = makeStyles(() => ({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--dim-1, 4px)',
    padding: 'var(--dim-1, 4px) var(--dim-2, 8px)',
    fontSize: 'var(--font-size-xs, 12px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-700, #555)',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'var(--theme-color-surface-d2, #e8e8e8)'
    }
  },
  triggerActive: {
    background: 'var(--theme-color-surface-d2, #e8e8e8)',
    color: 'var(--color-neutral-900, #222)'
  },
  chevron: {
    fontSize: 10,
    opacity: 0.7
  },
  countBadge: {
    marginLeft: 'var(--dim-1, 4px)',
    padding: '0 6px',
    height: 'var(--dim-4, 16px)',
    minWidth: 'var(--dim-4, 16px)',
    borderRadius: 8,
    background: 'var(--color-neutral-200, #e0e0e0)',
    color: 'var(--color-neutral-800, #333)',
    fontSize: 10,
    lineHeight: 'var(--dim-4, 16px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  inlineCount: {
    marginLeft: 6,
    padding: '0 6px',
    height: 'var(--dim-4, 16px)',
    minWidth: 'var(--dim-4, 16px)',
    borderRadius: 8,
    background: 'var(--color-primary-100, #d3e3fd)',
    color: 'var(--color-primary-800, #1a4d8c)',
    fontSize: 10,
    lineHeight: 'var(--dim-4, 16px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  subheader: {
    fontSize: 10,
    fontWeight: 'var(--font-weight-bold, 700)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-500, #888)',
    lineHeight: 1,
    padding: 'var(--dim-2, 8px) var(--dim-4, 16px) var(--dim-1, 4px)',
    background: 'transparent'
  },
  item: {
    paddingTop: 6,
    paddingBottom: 6,
    paddingRight: 'var(--dim-3, 12px)',
    minHeight: 36
  },
  itemIcon: {
    minWidth: 28,
    color: 'var(--color-neutral-700, #555)'
  },
  itemText: {
    margin: 0
  },
  itemTextPrimary: {
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center'
  },
  itemTextSecondary: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)'
  }
}))

// Stack-mode tools the user can add directly as blank instances.
//
// Research is exclusive and not added here.
//
// Note on `trend`: intentionally NOT included. Trend requires a primary
// `columnInstance` on its payload to render anything (the tile has no
// in-tile primary picker today -- the empty state just says
// "right-click a column header..."). Adding a "blank Trend" tile
// would land users on an unworkable placeholder. Re-add `'trend'`
// here once `TrendTool` exposes an empty-state primary-measure
// picker (using the existing `MeasureDropdown` +
// `MeasurePickerDialog` filtered to `isSeriesEligibleCatalog`).
// Users can still create a Trend via the Presets section below or
// by right-clicking a column / cell.
const STACK_TOOL_IDS = ['allocation', 'scatter']

// Minimum payload each chart type needs to produce a real request
// the first time it mounts (rather than the "no scope" empty state).
// Catalog presets resolve to similar shapes -- chart-type entries
// land users in the same place those presets do, just without any
// pre-picked measure / axes (the user picks via the toolbar).
const BLANK_TYPE_PAYLOADS = {
  allocation: { scope: 'level', depth: 0 },
  scatter: { scope: 'header' }
}

export default function ChartCatalogMenu ({ renderTrigger }) {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const { config } = usePortfolioData()
  const { isOpen: panelOpen, openPanel, closePanel } = useUtilityPanel()
  const { instances, addChart, instanceCountByCatalog } = useChartCatalogActions()

  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), [])
  const handleClose = useCallback(() => setAnchorEl(null), [])

  // Re-resolve on every menu open so stale eligibility decisions
  // (e.g. the user added a column since the last open) don't linger.
  const resolved = useMemo(() => {
    return CHART_CATALOG.map((entry) => {
      const r = resolveCatalogEntry(entry.id, { config })
      return { entry, ok: r.ok, reason: r.reason || null }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, anchorEl])

  const handleAddType = useCallback((toolId) => {
    setAnchorEl(null)
    addChart({ toolId, payload: BLANK_TYPE_PAYLOADS[toolId] })
    openPanel()
  }, [addChart, openPanel])

  const handleAddPreset = useCallback((entry) => {
    setAnchorEl(null)
    addChart({ catalogId: entry.id, toolId: entry.toolId })
    openPanel()
  }, [addChart, openPanel])

  const handleTogglePanel = useCallback(() => {
    setAnchorEl(null)
    if (panelOpen) closePanel(); else openPanel()
  }, [panelOpen, openPanel, closePanel])

  const triggerCtx = {
    isOpen: Boolean(anchorEl),
    disabled: false,
    savedCount: instances.length,
    onOpen: handleOpen
  }

  const triggerClassName = [
    classes.trigger,
    anchorEl ? classes.triggerActive : ''
  ].filter(Boolean).join(' ')

  return (
    <>
      {renderTrigger ? renderTrigger(triggerCtx) : (
        <button
          type='button'
          className={triggerClassName}
          onClick={handleOpen}
          aria-haspopup='menu'
          aria-expanded={Boolean(anchorEl)}
          title='Charts'
        >
          <span>Charts</span>
          {instances.length > 0 && (
            <span className={classes.countBadge} aria-label={`${instances.length} charts in view`}>
              {instances.length}
            </span>
          )}
          <span className={classes.chevron} aria-hidden='true'>▾</span>
        </button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem className={classes.item} onClick={handleTogglePanel}>
          <ListItemIcon className={classes.itemIcon}>
            {panelOpen
              ? <VisibilityOffIcon fontSize='small' />
              : <VisibilityIcon fontSize='small' />}
          </ListItemIcon>
          <ListItemText
            className={classes.itemText}
            primary={panelOpen ? 'Hide charts panel' : 'Show charts panel'}
            primaryTypographyProps={{ className: classes.itemTextPrimary }}
          />
        </MenuItem>
        <Divider />

        <ListSubheader disableSticky className={classes.subheader}>Chart types</ListSubheader>
        {STACK_TOOL_IDS.map(toolId => {
          const tool = TOOL_REGISTRY[toolId]
          if (!tool) return null
          return (
            <MenuItem
              key={`type_${toolId}`}
              className={classes.item}
              onClick={() => handleAddType(toolId)}
            >
              <ListItemIcon className={classes.itemIcon}>
                {tool.renderIcon ? tool.renderIcon() : null}
              </ListItemIcon>
              <ListItemText
                className={classes.itemText}
                primary={tool.label}
                secondary='Add a blank chart of this type'
                primaryTypographyProps={{ className: classes.itemTextPrimary }}
                secondaryTypographyProps={{ className: classes.itemTextSecondary }}
              />
            </MenuItem>
          )
        })}

        <Divider />
        <ListSubheader disableSticky className={classes.subheader}>Presets</ListSubheader>
        {resolved.map(({ entry, ok, reason }) => {
          const count = instanceCountByCatalog(entry.id)
          const primary = (
            <>
              <span>{entry.name}</span>
              {count > 0 && (
                <span className={classes.inlineCount} aria-label={`${count} in view`}>{count}</span>
              )}
            </>
          )
          const item = (
            <MenuItem
              key={`preset_${entry.id}`}
              className={classes.item}
              disabled={!ok}
              onClick={ok ? () => handleAddPreset(entry) : undefined}
            >
              <ListItemIcon className={classes.itemIcon}>
                {entry.renderIcon ? entry.renderIcon() : null}
              </ListItemIcon>
              <ListItemText
                className={classes.itemText}
                primary={primary}
                secondary={ok ? entry.description : reason}
                primaryTypographyProps={{ className: classes.itemTextPrimary, component: 'div' }}
                secondaryTypographyProps={{ className: classes.itemTextSecondary }}
              />
            </MenuItem>
          )
          // MUI swallows tooltips on disabled items; wrap in a span.
          if (!ok && reason) {
            return (
              <Tooltip key={`preset_${entry.id}`} title={reason} placement='right' enterDelay={400}>
                <span style={{ display: 'block' }}>{item}</span>
              </Tooltip>
            )
          }
          return item
        })}
      </Menu>
    </>
  )
}

ChartCatalogMenu.propTypes = {
  // Custom trigger renderer. Receives { isOpen, disabled, savedCount, onOpen }.
  // When omitted, the default `Charts` toolbar button is rendered.
  renderTrigger: PropTypes.func
}
